/**
 * Lot S (13/10/2026) : personnes de l'équipe retrouvées parmi les membres, sans adresse écrite en dur.
 *
 * Le modèle d'étapes par défaut visait `ines@`, `stephane@` et `mmagne@evolusite.fr` ; les vraies adresses
 * étaient autres, et ces valeurs inconnues s'affichaient telles quelles à côté des vrais membres (doublons
 * apparents dans « Une fois cochée, prévenir »). Le modèle par défaut désigne maintenant une personne par son
 * prénom (`team:ines`…), résolu au moment de la lecture vers le membre correspondant.
 */

export type TeamKey = 'ines' | 'stephane' | 'admin';

export interface TeamHint {
  /** Prénom attendu (accents et casse ignorés), premier mot du nom du membre. */
  firstName: string;
  /** Initiale attendue, si le prénom ne départage pas. */
  initial: string;
  /** Repli : le seul administrateur actif (Maxime). */
  admin?: boolean;
}

export const TEAM_HINTS: Record<TeamKey, TeamHint> = {
  ines: { firstName: 'Inès', initial: 'I' },
  stephane: { firstName: 'Stéphane', initial: 'S' },
  admin: { firstName: 'Maxime', initial: 'M', admin: true },
};

/** Ce qu'il faut savoir d'un membre pour le retrouver (DTO `User` ou ligne `users`). */
export interface TeamMemberRef {
  email: string;
  name: string;
  initials: string;
  role: 'ADMIN' | 'MEMBER';
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  /** Connu pour s'être déjà connecté : départage deux homonymes (un doublon jamais connecté). */
  hasLoggedIn?: boolean;
}

/** Minuscules, sans accents ni espaces autour : « Inès » → « ines ». */
export function foldName(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();
}

/** Prénom d'un membre : le premier mot de son nom, sauf si ce nom est une adresse e-mail. */
export function firstNameOf(name: string): string {
  const first = foldName(name).split(/\s+/)[0] ?? '';
  return first.includes('@') ? '' : first;
}

/** Un seul candidat, ou un seul parmi ceux qui se sont déjà connectés ; sinon personne (ambigu). */
function single<T extends TeamMemberRef>(candidates: T[]): T | null {
  if (candidates.length === 1) return candidates[0]!;
  if (candidates.length === 0) return null;
  const loggedIn = candidates.filter((m) => m.hasLoggedIn);
  return loggedIn.length === 1 ? loggedIn[0]! : null;
}

/**
 * Le membre (non désactivé) désigné par `hint` : par le prénom, sinon par l'initiale, sinon (administrateur)
 * le seul administrateur actif. Plusieurs candidats sans moyen de les départager : null.
 */
export function findTeamMember<T extends TeamMemberRef>(hint: TeamHint, members: readonly T[]): T | null {
  const usable = members.filter((m) => m.status !== 'DISABLED');
  const byName = usable.filter((m) => firstNameOf(m.name) === foldName(hint.firstName));
  if (byName.length > 0) return single(byName);
  const byInitial = usable.filter((m) => foldName(m.initials) === foldName(hint.initial));
  const found = single(byInitial);
  if (found) return found;
  if (hint.admin) return single(usable.filter((m) => m.role === 'ADMIN' && m.status === 'ACTIVE'));
  return null;
}
